"use client";

import { cn } from "@/lib/utils";
import { type VariantProps, cva } from "class-variance-authority";
import { forwardRef } from "react";
import type React from "react";
import { IconWrapper } from "./custom/icon-wrapper";

const listVariants = cva("w-full", {
  variants: {
    variant: {
      default: "space-y-3",
      divided: "divide-y divide-border",
      flush: "space-y-0",
    },
    size: {
      sm: "text-sm",
      md: "text-base",
      lg: "text-lg",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "md",
  },
});

const listItemVariants = cva(
  "flex items-center w-full transition-colors cursor-default",
  {
    variants: {
      variant: {
        default: "rounded-xl border border-border hover:bg-accent/50",
        frosted:
          "rounded-xl border border-border backdrop-blur-md bg-background/30 hover:bg-background/40",
        transparent: "hover:bg-accent/30",
        button:
          "hover:bg-accent cursor-pointer focus-visible:bg-accent focus-visible:outline-none",
      },
      size: {
        sm: "px-2 py-1.5",
        md: "px-3 py-2.5",
        lg: "px-4 py-3",
      },
      disabled: {
        true: "opacity-50 cursor-not-allowed hover:bg-transparent",
        false: "",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
      disabled: false,
    },
  },
);

const listItemIconVariants = cva("flex items-center justify-center shrink-0");

const listItemTextVariants = cva("flex flex-col min-w-0 flex-1", {
  variants: {
    size: {
      sm: "gap-0.5",
      md: "gap-1",
      lg: "gap-1.5",
    },
  },
  defaultVariants: {
    size: "md",
  },
});

export interface ListProps
  extends React.HTMLAttributes<HTMLUListElement>,
    VariantProps<typeof listVariants> {}

export interface ListItemProps
  extends React.HTMLAttributes<HTMLLIElement>,
    VariantProps<typeof listItemVariants> {
  icon?: React.ReactNode;
  footer?: React.ReactNode;
}

export interface ListItemIconProps
  extends React.HTMLAttributes<HTMLDivElement> {
  variant?: "default" | "frosted";
  size?: "sm" | "md" | "lg" | "xl";
}

export interface ListItemTextProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof listItemTextVariants> {
  primary?: React.ReactNode;
  secondary?: React.ReactNode;
}

export interface ListItemActionsProps
  extends React.HTMLAttributes<HTMLDivElement> {}

const ListRoot = forwardRef<HTMLUListElement, ListProps>(
  ({ className, variant, size, ...props }, ref) => (
    <ul
      ref={ref}
      className={cn(listVariants({ variant, size }), className)}
      {...props}
    />
  ),
);

const ListItem = forwardRef<HTMLLIElement, ListItemProps>(
  (
    { className, variant, size, disabled, icon, footer, children, ...props },
    ref,
  ) => (
    <li
      ref={ref}
      className={cn(
        listItemVariants({ variant, size, disabled }),
        footer && "flex-col items-start",
        className,
      )}
      {...props}
    >
      <div
        className={cn(
          "flex items-center w-full",
          size === "sm" ? "gap-2" : size === "lg" ? "gap-4" : "gap-3",
        )}
      >
        {icon && (
          <div className={cn(listItemIconVariants())}>
            <IconWrapper
              variant={variant === "frosted" ? "frosted" : "default"}
              size="lg"
            >
              {icon}
            </IconWrapper>
          </div>
        )}
        {children}
      </div>
      {footer && (
        <div className="w-full flex justify-center px-4 pt-2">{footer}</div>
      )}
    </li>
  ),
);

const ListItemIcon = forwardRef<HTMLDivElement, ListItemIconProps>(
  (
    { className, size = "lg", variant = "default", children, ...props },
    ref,
  ) => (
    <div ref={ref} className={cn(listItemIconVariants(), className)} {...props}>
      <IconWrapper variant={variant} size={size}>
        {children}
      </IconWrapper>
    </div>
  ),
);

const ListItemText = forwardRef<HTMLDivElement, ListItemTextProps>(
  ({ className, size, primary, secondary, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(listItemTextVariants({ size }), className)}
      {...props}
    >
      {primary && (
        <div className="font-suisse font-medium text-sm text-foreground leading-5 cursor-default truncate">
          {primary}
        </div>
      )}
      {secondary && (
        <div className="font-suisse text-xs text-muted-foreground leading-4 cursor-default">
          {secondary}
        </div>
      )}
      {children}
    </div>
  ),
);

const ListItemActions = forwardRef<HTMLDivElement, ListItemActionsProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("flex flex-col gap-2 shrink-0 items-end", className)}
      {...props}
    />
  ),
);

export {
  ListRoot as List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemActions,
  listItemTextVariants,
};
